import ks from "../ReUsableTS/kBrowserShared";
import { kMotion } from "./kMotion";
import type { kModalDialog } from "./kModalDialog";

/**
 * Member bodies of kModalDialog moved out of kModalDialog.ts (size split, 2026-10-20). kModalDialog keeps every
 * member with the same signature as a one-line delegate here; private state is reached by element access
 * (Dlg["_X"]), and the class itself is passed in (Cls) for its static state, so there is no runtime import
 * back to kModalDialog.ts.
 */
export class kModalDialogStatics {
    /** Body of kModalDialog.PrefersReducedMotion (moved verbatim). */
    public static PrefersReducedMotion(): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            return kMotion.IsReduced();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogStatics.PrefersReducedMotion");
            return false;
        }
    }
    /** Body of kModalDialog.AddOpenStateObserver (moved verbatim). */
    public static AddOpenStateObserver(Cls: typeof kModalDialog, Observer: (AnyOpen: boolean) => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Cls["_OpenStateObservers"].indexOf(Observer) < 0) {
                Cls["_OpenStateObservers"].push(Observer);
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogStatics.AddOpenStateObserver"); }
    }
    /** Body of kModalDialog.AnyOpen (moved verbatim). */
    public static AnyOpen(Cls: typeof kModalDialog): boolean {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return false; }
        try {
            return Cls["_OpenStack"].length > 0;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogStatics.AnyOpen");
            return false;
        }
    }
    /** Body of kModalDialog.RemoveOpenStateObserver (moved verbatim). */
    public static RemoveOpenStateObserver(Cls: typeof kModalDialog, Observer: (AnyOpen: boolean) => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Index: number = Cls["_OpenStateObservers"].indexOf(Observer);
            if (Index >= 0) { Cls["_OpenStateObservers"].splice(Index, 1); }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogStatics.RemoveOpenStateObserver"); }
    }
    /** Fire every observer with the current open state (true while ≥1 dialog is on the stack).
     *  Called after a push (Show) and after a pop (_RemoveFromStack), so observers see every
     *  transition. ks.DoCallBack isolates a throwing observer from the others. */
    // ^ moved with kModalDialog._NotifyOpenState
    public static _NotifyOpenState(Cls: typeof kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const AnyOpen: boolean = Cls["_OpenStack"].length > 0;
            // A body-level marker any app can style off: while a modal is open the surface it
            // covers can drop expensive-but-now-invisible work (e.g. backdrop-filter blurs on
            // panels hidden behind the dim scrim) so the modal's frame composites cheaply.
            if (typeof document !== "undefined" && document.body !== null) {
                document.body.classList.toggle("kdialog-open", AnyOpen);
            }
            for (const Observer of Cls["_OpenStateObservers"]) { ks.DoCallBack(Observer, AnyOpen); }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogStatics._NotifyOpenState"); }
    }
    /** Add a space-separated class list to an element. classList.add rejects a string containing
     *  spaces (InvalidCharacterError), so the list is split; empty entries from double spaces are
     *  skipped rather than thrown on. */
    // ^ moved with kModalDialog._AddClasses
    public static _AddClasses(Target: HTMLElement, ClassNames: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Parts: string[] = ks.Nz(ClassNames).split(" ");
            for (const Part of Parts) {
                if (Part !== "") { Target.classList.add(Part); }
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogStatics._AddClasses"); }
    }
}
